import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import TopNav from "@/components/TopNav";
import Login from "@/components/Login";
import Onboarding from "@/components/Onboarding";
import CreateQuote from "@/components/CreateQuote";
import QuoteEditor from "@/components/QuoteEditor";
import SavedQuotes from "@/components/SavedQuotes";
import Catalog from "@/components/Catalog";
import WhatsAppConnect from "@/components/WhatsAppConnect";
import SubscriptionPage from "@/components/SubscriptionPage";
import UpgradePage from "@/components/UpgradePage";
import { GoogleDots } from "@/components/GoogleBits";
import UpgradeModal from "@/components/UpgradeModal";
import { PlanContext } from "@/context/PlanContext";
import { getMe, exchangeSession, logout as apiLogout, listCompanies, createCompany, updateCompany, getMyPlan, paymentStatus, setUserFlag } from "@/lib/api";
import ExpectationPopup from "@/components/ExpectationPopup";
import WhatsAppUpsellPopup from "@/components/WhatsAppUpsellPopup";
import { getActiveCompanyId, setActiveCompanyId, companyToProfile, onboardingToCompany } from "@/lib/storage";
import { getPendingPrompt, clearPendingPrompt, capturePromptFromUrl, peekAuthTab } from "@/lib/authFlow";
import { track, trackScreen } from "@/lib/analytics";
import CookieConsent from "@/components/CookieConsent";
import { toast } from "sonner";

function App() {
  const [authUser, setAuthUser] = useState(null); // null=checking, false=unauth, obj=user
  const [companies, setCompanies] = useState([]);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [activeId, setActiveId] = useState(getActiveCompanyId());
  const [view, setView] = useState("home");
  const [onboarding, setOnboarding] = useState(null); // null | {mode:'new'} | {mode:'edit', id}
  const [editorState, setEditorState] = useState(null);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [planData, setPlanData] = useState({ plan: "free", limits: null, usage: null, subscription: null });
  const [upgradeReason, setUpgradeReason] = useState(null);
  const exchangedRef = useRef(false);
  const savingRef = useRef(false);
  const paymentHandledRef = useRef(false);

  const refreshPlan = useCallback(async () => {
    try { const d = await getMyPlan(); setPlanData(d); } catch {}
  }, []);
  const openUpgrade = useCallback((reason) => setUpgradeReason(reason || "general"), []);

  // GA4 virtual page views for the SPA (login screen, onboarding, each app view)
  useEffect(() => {
    if (authUser === null) return;
    trackScreen(authUser === false ? "login" : onboarding ? "onboarding" : view);
  }, [authUser, onboarding, view]);

  const loadCompanies = useCallback(async () => {
    const list = await listCompanies();
    setCompanies(list);
    setCompaniesLoaded(true);
    return list;
  }, []);

  // Auth bootstrap: handle OAuth callback hash first, else verify session.
  useEffect(() => {
    const boot = async () => {
      capturePromptFromUrl();
      const hash = window.location.hash || "";
      if (hash.includes("session_id=") && !exchangedRef.current) {
        exchangedRef.current = true;
        const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
        try {
          const u = await exchangeSession(sid);
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          track(u.is_new ? "sign_up" : "login", { method: "google" });
          setAuthUser(u);
          return;
        } catch {
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
        }
      }
      try {
        const u = await getMe();
        setAuthUser(u);
      } catch {
        setAuthUser(false);
      }
    };
    boot();
  }, []);

  // Once authenticated, load companies + pick up any pending hero prompt.
  useEffect(() => {
    if (!authUser) return;
    const p = getPendingPrompt();
    if (p) { setPendingPrompt(p); clearPendingPrompt(); }
    refreshPlan();
    loadCompanies().catch(() => setCompaniesLoaded(true));
  }, [authUser, loadCompanies, refreshPlan]);

  // Handle Stripe checkout return (success / cancel).
  useEffect(() => {
    if (!authUser || paymentHandledRef.current) return;
    const path = window.location.pathname;
    if (path.startsWith("/payment/success")) {
      paymentHandledRef.current = true;
      const sid = new URLSearchParams(window.location.search).get("session_id");
      (async () => {
        let ok = false;
        for (let i = 0; i < 6 && sid; i++) {
          try {
            const st = await paymentStatus(sid);
            if (st.payment_status === "paid") {
              ok = true;
              if (st.conversion) track(st.conversion.kind, { lookup_key: st.conversion.lookup_key, value: st.conversion.value, currency: st.conversion.currency, transaction_id: sid });
              break;
            }
          } catch {}
          await new Promise((r) => setTimeout(r, 1500));
        }
        window.history.replaceState(null, "", "/");
        await refreshPlan();
        toast.success(ok ? "Assinatura ativada! Aproveite os novos recursos." : "Pagamento em processamento. Seu plano será atualizado em instantes.");
      })();
    } else if (path.startsWith("/payment/cancel")) {
      paymentHandledRef.current = true;
      window.history.replaceState(null, "", "/");
      toast("Checkout cancelado.");
    }
  }, [authUser, refreshPlan]);

  // Resolve active company once companies load.
  useEffect(() => {
    if (!companiesLoaded) return;
    if (companies.length === 0) {
      setOnboarding({ mode: "new" });
      return;
    }
    const valid = companies.find((c) => c.id === activeId);
    if (!valid) {
      setActiveId(companies[0].id);
      setActiveCompanyId(companies[0].id);
    }
  }, [companiesLoaded, companies]); // eslint-disable-line

  const activeCompany = companies.find((c) => c.id === activeId);
  const profile = companyToProfile(activeCompany);

  const [waUpsell, setWaUpsell] = useState(false); // false | "first_quote" | "model_approved"
  const flags = authUser?.flags || {};
  const markFlag = (key) => {
    setAuthUser((u) => (u ? { ...u, flags: { ...(u.flags || {}), [key]: true } } : u));
    setUserFlag(key).catch(() => {});
  };
  const WA_UPSELL_FLAG = { first_quote: "wa_upsell_seen", model_approved: "wa_upsell_model_seen" };

  const goEditorNew = (data) => {
    setEditorState({ initialData: data });
    setView("editor");
    if (!flags.ga_first_quote) { track("first_quote"); markFlag("ga_first_quote"); }
    if (!flags.wa_upsell_seen) setTimeout(() => setWaUpsell("first_quote"), 1400);
  };
  const closeWaUpsell = () => { if (waUpsell) markFlag(WA_UPSELL_FLAG[waUpsell]); setWaUpsell(false); };
  const waUpsellPrimary = () => {
    closeWaUpsell();
    setView(planValue.limits && planValue.limits.whatsapp ? "whatsapp" : "upgrade");
  };
  const goEditorExisting = (quote) => { setEditorState({ existing: quote }); setView("editor"); };

  const handleOnboardingDone = async (p) => {
    if (savingRef.current) return; // prevent duplicate companies from double submit
    savingRef.current = true;
    const payload = onboardingToCompany(p);
    try {
      if (onboarding?.mode === "edit") {
        const cid = onboarding.id || activeId;
        if (cid) {
          await updateCompany(cid, payload);
          await loadCompanies();
          toast.success("Modelo atualizado!");
        }
      } else {
        const created = await createCompany(payload);
        await loadCompanies();
        setActiveId(created.id);
        setActiveCompanyId(created.id);
        refreshPlan();
        toast.success("Empresa criada! Pronto para orçar.");
        if (!flags.wa_upsell_model_seen) setTimeout(() => setWaUpsell("model_approved"), 900);
      }
      setOnboarding(null);
      setView("home");
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "limit_companies") { setOnboarding(null); openUpgrade("companies"); }
      else if (code === "premium_model") { setOnboarding(null); openUpgrade("model"); }
      else toast.error("Não foi possível salvar. Tente novamente.");
    } finally {
      savingRef.current = false;
    }
  };

  const renameCompany = async (id, name) => {
    const c = companies.find((x) => x.id === id);
    if (!c || !name.trim()) return;
    const nm = name.trim();
    try {
      await updateCompany(id, {
        name: nm,
        company: { ...(c.company || {}), name: nm },
        theme: c.theme || {},
        layout: c.layout || "minimal_classic",
        doc: c.doc || {},
        logo: c.logo || null,
      });
      await loadCompanies();
      toast.success("Empresa renomeada");
    } catch {
      toast.error("Não foi possível renomear");
    }
  };

  const switchCompany = (id) => {
    setActiveId(id);
    setActiveCompanyId(id);
    setView("home");
  };

  const handleNewCompany = () => {
    const lim = planData.limits?.companies;
    if (lim != null && companies.length >= lim) return openUpgrade("companies");
    setOnboarding({ mode: "new" });
  };

  const handleQuoteSaved = () => { refreshPlan(); };

  const handleLogout = async () => {
    try { await apiLogout(); } catch {}
    setActiveCompanyId(null);
    setAuthUser(false);
    setCompanies([]);
    setCompaniesLoaded(false);
    setActiveId(null);
  };

  const handleNav = (v) => {
    if (v === "model") return setOnboarding({ mode: "edit", id: activeId });
    if (v === "whatsapp" && planData.limits && !planData.limits.whatsapp) return openUpgrade("whatsapp");
    if (v === "upgrade" || v === "subscription") setOnboarding(null);
    setView(v);
  };

  // ---------- Render ----------
  if (authUser === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#fafafa]">
        <GoogleDots />
      </div>
    );
  }

  if (authUser === false) {
    return (
      <>
        <Login onAuthed={setAuthUser} pendingPrompt={getPendingPrompt()} initialTab={peekAuthTab()} />
        <CookieConsent />
        <Toaster position="top-center" />
      </>
    );
  }

  // ---------- Authenticated ----------
  const planValue = {
    plan: planData.plan,
    trialEligible: planData.trial_eligible,
    limits: planData.limits,
    usage: planData.usage,
    subscription: planData.subscription,
    refresh: refreshPlan,
    openUpgrade,
    isAllowedModel: (m) => (!planData.limits ? true : (planData.limits.models || []).includes(m)),
    canPersonalize: planData.limits ? planData.limits.personalization === "full" : true,
  };

  const loadingCompanies = !companiesLoaded && !onboarding;
  const initialOnb = onboarding && onboarding.mode === "edit" ? profile : null;

  return (
    <PlanContext.Provider value={planValue}>
      <div className="App min-h-screen bg-[#fafafa]">
        {!loadingCompanies && (
          <TopNav
            view={view}
            onNav={handleNav}
            user={authUser}
            companies={companies}
            activeCompanyId={activeId}
            onSwitchCompany={switchCompany}
            onNewCompany={handleNewCompany}
            onRenameCompany={renameCompany}
            onLogout={handleLogout}
            planName={planValue.plan}
            subscription={planValue.subscription}
            waLocked={planValue.limits ? !planValue.limits.whatsapp : false}
          />
        )}

        {loadingCompanies && (
          <div className="min-h-screen grid place-items-center">
            <GoogleDots />
          </div>
        )}

        {!loadingCompanies && onboarding && (
          <Onboarding
            key={onboarding.id || "new"}
            initial={initialOnb}
            mode={onboarding.mode}
            allowCancel={companies.length > 0}
            onCancel={() => setOnboarding(null)}
            onDone={handleOnboardingDone}
          />
        )}

        {!loadingCompanies && !onboarding && (
          <>
            {view === "home" && activeCompany && (
              <CreateQuote
                profile={profile}
                companyId={activeId}
                onComplete={goEditorNew}
                userName={authUser?.name}
                companyName={activeCompany?.name}
                initialPrompt={pendingPrompt}
                onConsumePrompt={() => setPendingPrompt(null)}
              />
            )}

            {view === "saved" && activeCompany && (
              <SavedQuotes companyId={activeId} onOpen={goEditorExisting} onNew={() => setView("home")} />
            )}

            {view === "catalog" && activeCompany && (
              <Catalog companyId={activeId} companyName={activeCompany?.name} />
            )}

            {view === "whatsapp" && activeCompany && (
              <WhatsAppConnect profile={profile} onSkip={() => setView("home")} />
            )}

            {view === "subscription" && (
              <SubscriptionPage onBack={() => { setView("home"); if (companies.length === 0) setOnboarding({ mode: "new" }); }} />
            )}

            {view === "upgrade" && (
              <UpgradePage onBack={() => { setView("home"); if (companies.length === 0) setOnboarding({ mode: "new" }); }} />
            )}

            {view === "editor" && editorState && activeCompany && (
              <QuoteEditor
                profile={profile}
                companyId={activeId}
                existing={editorState.existing}
                initialData={editorState.initialData}
                onBack={() => setView(editorState.existing ? "saved" : "home")}
                onSaved={handleQuoteSaved}
              />
            )}
          </>
        )}

        {authUser && !flags.expectation_asked && <ExpectationPopup userName={authUser.name} onDone={() => markFlag("expectation_asked")} />}
        {waUpsell && <WhatsAppUpsellPopup moment={waUpsell} isPaid={!!(planValue.limits && planValue.limits.whatsapp)} waLinked={false} onPrimary={waUpsellPrimary} onClose={closeWaUpsell} />}
        {upgradeReason && <UpgradeModal reasonKey={upgradeReason} onClose={() => setUpgradeReason(null)} onSeePlans={() => { setUpgradeReason(null); setOnboarding(null); setView("upgrade"); }} />}
        <CookieConsent />
        <Toaster position="top-center" />
      </div>
    </PlanContext.Provider>
  );
}

export default App;
